// Page de création du profil Pro
// Première étape pour accéder à l'Espace Pro

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2, Store, Wrench, ArrowRight, ArrowLeft, Check,
  MapPin, Phone, Mail, FileText, User
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { sellerProfileAPI } from '../../services/sellerAPI';

// Étape 1: Type de profil
function StepType({ data, setData, onNext }) {
  const types = [
    {
      id: 'marketplace',
      icon: Store,
      title: 'Vendeur Marketplace',
      description: 'Je vends des produits en ligne',
      color: '#F97316'
    },
    {
      id: 'artisan',
      icon: Wrench,
      title: 'Artisan / Prestataire',
      description: 'Je propose des services et interventions',
      color: '#8B5CF6'
    },
    {
      id: 'both',
      icon: Building2,
      title: 'Les deux',
      description: 'Je vends des produits ET propose des services',
      color: '#3B82F6'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Quel type de professionnel es-tu ?</h2>
        <p className="text-gray-500 mt-2">Choisis le type qui correspond à ton activité</p>
      </div>

      <div className="grid gap-4">
        {types.map((type) => {
          const Icon = type.icon;
          const isSelected = data.seller_type === type.id;
          
          return (
            <motion.button
              key={type.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setData({...data, seller_type: type.id})}
              className={`flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                isSelected 
                  ? 'border-orange-500 bg-orange-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div 
                className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  isSelected ? 'bg-orange-500' : 'bg-gray-100'
                }`}
              >
                <Icon size={24} className={isSelected ? 'text-white' : 'text-gray-500'} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{type.title}</h3>
                <p className="text-sm text-gray-500">{type.description}</p>
              </div>
              {isSelected && (
                <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                  <Check size={14} className="text-white" />
                </div>
              )}
            </motion.button>
          );
        })}
      </div>

      <Button
        onClick={onNext}
        disabled={!data.seller_type}
        className="w-full bg-orange-500 hover:bg-orange-600 rounded-xl h-12 mt-6"
      >
        Continuer <ArrowRight size={18} className="ml-2" />
      </Button>
    </div>
  );
}

// Étape 2: Informations de base
function StepInfo({ data, setData, onNext, onBack }) {
  const isValid = data.shop_name && data.email;

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Informations de ton entreprise</h2>
        <p className="text-gray-500 mt-2">Ces informations seront visibles sur ton profil</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom de l'entreprise / Boutique *
          </label>
          <div className="relative">
            <Store size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={data.shop_name}
              onChange={(e) => setData({...data, shop_name: e.target.value})}
              placeholder="Ma Super Boutique"
              className="pl-10 rounded-xl"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description (optionnel)
          </label>
          <textarea
            value={data.description || ''}
            onChange={(e) => setData({...data, description: e.target.value})}
            placeholder="Décris ton activité en quelques mots..."
            rows={3}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="email"
                value={data.email}
                onChange={(e) => setData({...data, email: e.target.value})}
                placeholder="contact@entreprise.com"
                className="pl-10 rounded-xl"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
            <div className="relative">
              <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="tel"
                value={data.phone || ''}
                onChange={(e) => setData({...data, phone: e.target.value})}
                placeholder="06 12 34 56 78"
                className="pl-10 rounded-xl"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3 mt-6">
        <Button variant="outline" onClick={onBack} className="flex-1 rounded-xl h-12">
          <ArrowLeft size={18} className="mr-2" /> Retour
        </Button>
        <Button
          onClick={onNext}
          disabled={!isValid}
          className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-xl h-12"
        >
          Continuer <ArrowRight size={18} className="ml-2" />
        </Button>
      </div>
    </div>
  );
}

// Étape 3: Adresse et légal
function StepAddress({ data, setData, onSubmit, onBack, loading }) {
  const departments = [
    "01 - Ain", "02 - Aisne", "03 - Allier", "75 - Paris", "77 - Seine-et-Marne",
    "78 - Yvelines", "91 - Essonne", "92 - Hauts-de-Seine", "93 - Seine-Saint-Denis",
    "94 - Val-de-Marne", "95 - Val-d'Oise", "69 - Rhône", "13 - Bouches-du-Rhône"
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Adresse et informations légales</h2>
        <p className="text-gray-500 mt-2">Ces informations sont optionnelles mais recommandées</p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
          <div className="relative">
            <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={data.address_line1 || ''}
              onChange={(e) => setData({...data, address_line1: e.target.value})}
              placeholder="123 Rue des Artisans"
              className="pl-10 rounded-xl"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
            <Input
              value={data.city || ''}
              onChange={(e) => setData({...data, city: e.target.value})}
              placeholder="Paris"
              className="rounded-xl"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
            <Input
              value={data.postal_code || ''}
              onChange={(e) => setData({...data, postal_code: e.target.value})}
              placeholder="75001"
              className="rounded-xl"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Département</label>
          <select
            value={data.department || ''}
            onChange={(e) => setData({...data, department: e.target.value})}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white"
          >
            <option value="">Sélectionner un département</option>
            {departments.map(dep => (
              <option key={dep} value={dep.split(' - ')[0]}>{dep}</option>
            ))}
          </select>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <FileText size={16} className="inline mr-2" />
            N° SIRET (optionnel)
          </label>
          <Input
            value={data.siret || ''}
            onChange={(e) => setData({...data, siret: e.target.value})}
            placeholder="123 456 789 00012"
            className="rounded-xl"
          />
          <p className="text-xs text-gray-400 mt-1">
            Le SIRET sera vérifié pour valider ton compte professionnel
          </p>
        </div>
      </div>

      <div className="flex gap-3 mt-6">
        <Button variant="outline" onClick={onBack} className="flex-1 rounded-xl h-12">
          <ArrowLeft size={18} className="mr-2" /> Retour
        </Button>
        <Button
          onClick={onSubmit}
          disabled={loading}
          className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-xl h-12"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              Créer mon espace <Check size={18} className="ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export default function CreationProfilPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    seller_type: '',
    shop_name: '',
    description: '',
    email: '',
    phone: '',
    address_line1: '',
    city: '',
    postal_code: '',
    department: '',
    siret: ''
  });

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await sellerProfileAPI.create(data);
      toast.success('Espace Pro créé avec succès !');
      navigate('/compte');
    } catch (e) {
      toast.error(e.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
            <Building2 size={32} className="text-white" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Créer ton Espace Pro</h1>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-2 rounded-full transition-all ${
                s === step ? 'w-8 bg-orange-500' : 
                s < step ? 'w-8 bg-orange-300' : 
                'w-8 bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          {step === 1 && (
            <StepType data={data} setData={setData} onNext={() => setStep(2)} />
          )}
          {step === 2 && (
            <StepInfo 
              data={data} 
              setData={setData} 
              onNext={() => setStep(3)} 
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && (
            <StepAddress 
              data={data} 
              setData={setData} 
              onSubmit={handleSubmit}
              onBack={() => setStep(2)}
              loading={loading}
            />
          )}
        </div>

        {/* Retour */}
        <button
          onClick={() => navigate('/')}
          className="w-full text-center text-gray-500 hover:text-gray-700 py-4 mt-4 text-sm"
        >
          Retour à l'accueil
        </button>
      </motion.div>
    </div>
  );
}
